import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Building2, Users, ClipboardList, DollarSign, Activity, ShieldCheck } from "lucide-react";

export default function SuperAdmin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [audit, setAudit] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [s, t, a] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/tenants"),
          api.get("/admin/audit"),
        ]);
        setStats(s.data);
        setTenants(t.data.items);
        setAudit(a.data.items);
      } catch (e) { toast.error(apiError(e)); }
    })();
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 space-y-8">
      <div>
        <div className="text-xs uppercase tracking-widest text-teal-400 mb-1">Super Admin — vixvox</div>
        <h1 className="font-display text-3xl font-bold text-white">Painel da plataforma</h1>
        <p className="text-slate-400 text-sm mt-1">Olá, {user?.name}</p>
      </div>

      {stats && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Empresas contratantes", value: stats.tenants, icon: Building2, color: "indigo" },
            { label: "Assinaturas ativas", value: stats.active_subscriptions, icon: Activity, color: "teal" },
            { label: "MRR (R$)", value: stats.mrr.toLocaleString("pt-BR", { minimumFractionDigits: 2 }), icon: DollarSign, color: "emerald" },
            { label: "Denúncias registradas", value: stats.complaints, icon: ClipboardList, color: "amber" },
          ].map((k, i) => (
            <Card key={i} data-testid={`admin-kpi-${i}`} className="p-5 bg-slate-900/60 border-slate-800">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-slate-400">{k.label}</div>
                <k.icon className={`w-4 h-4 text-${k.color}-400`} />
              </div>
              <div className="font-display text-3xl font-bold text-white">{k.value}</div>
            </Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="tenants">
        <TabsList className="bg-slate-900 border border-slate-800">
          <TabsTrigger value="tenants" data-testid="admin-tab-tenants">Empresas</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Trilha ISO 27001</TabsTrigger>
        </TabsList>
        <TabsContent value="tenants">
          <Card className="p-6 bg-slate-900/60 border-slate-800">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-slate-400 text-left border-b border-slate-800">
                    <th className="py-3 px-2 font-medium">Empresa</th>
                    <th className="py-3 px-2 font-medium">Slug</th>
                    <th className="py-3 px-2 font-medium">Plano</th>
                    <th className="py-3 px-2 font-medium">Status</th>
                    <th className="py-3 px-2 font-medium">Denúncias</th>
                    <th className="py-3 px-2 font-medium">Usuários</th>
                    <th className="py-3 px-2 font-medium">Criada em</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((t) => (
                    <tr key={t.id} className="border-b border-slate-800/60 hover:bg-slate-800/40">
                      <td className="py-3 px-2 text-white font-medium">{t.name}</td>
                      <td className="py-3 px-2 font-mono text-teal-300">{t.slug}</td>
                      <td className="py-3 px-2 text-slate-300">R$ {t.plan_price?.toFixed(2)}</td>
                      <td className="py-3 px-2">
                        <Badge className={t.subscription_status === "active" ? "bg-teal-500/20 text-teal-300 border-teal-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}>
                          {t.subscription_status === "trial" ? "Trial" : "Ativa"}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-slate-400">{t.complaints}</td>
                      <td className="py-3 px-2 text-slate-400">{t.users}</td>
                      <td className="py-3 px-2 text-slate-400">{new Date(t.created_at).toLocaleDateString("pt-BR")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
        <TabsContent value="audit">
          <Card className="p-6 bg-slate-900/60 border-slate-800">
            <div className="flex items-center gap-2 mb-4 text-slate-300 text-sm">
              <ShieldCheck className="w-4 h-4 text-teal-400" /> Trilha de auditoria imutável (ISO/IEC 27001 A.12.4)
            </div>
            <div className="space-y-2 max-h-[500px] overflow-y-auto">
              {audit.length === 0 && <div className="text-center text-slate-500 py-8">Sem eventos ainda.</div>}
              {audit.map((a) => (
                <div key={a.id} className="p-3 rounded-lg bg-slate-800/60 border border-slate-700 text-sm flex items-center justify-between gap-4">
                  <div>
                    <div className="text-white font-medium">{a.action}</div>
                    <div className="text-xs text-slate-400">{a.actor_email || "sistema"} → {a.target}</div>
                  </div>
                  <div className="text-xs text-slate-500 font-mono">{new Date(a.created_at).toLocaleString("pt-BR")}</div>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
